/**
 * The server prefix, kept across reloads so a catch-up is a delta and not a
 * replay of everything that ever happened.
 *
 * ## Why this exists
 *
 * `SyncClient` starts every session at `#version = 0` with an empty
 * `#confirmed`, so its first `pull` asks for the whole log. The Durable Object
 * answers with a delta whenever the request sits inside the retained window
 * (`LOG_RETENTION`, 2000 commands), which for most workspaces means *every
 * command they have ever dispatched*, re-applied on the main thread, on every
 * load — while the fully-formed workspace the browser just hydrated from its
 * own snapshot is thrown away and rebuilt.
 *
 * It also had a cliff, and it ran the wrong way: a workspace of 2001 commands
 * gets one snapshot frame and no replay, so it opened *faster* than one of 983.
 *
 * Remembering where the prefix got to turns the common case — nothing changed
 * since last time — into a pull that answers with an empty delta.
 *
 * ## Why it is not the snapshot `persistence.ts` already writes
 *
 * That record is the **optimistic** workspace: the confirmed prefix with the
 * outbox replayed over it, which is what the screen shows. This one is the
 * confirmed prefix alone, which is the only thing a version number can honestly
 * be attached to. Storing one and calling it the other would resume a session
 * claiming the server had already accepted commands still sitting in the
 * outbox — and the next delta would apply on top of them.
 *
 * ## What makes it safe to trust on the next boot
 *
 * Nothing here is trusted blindly. The record carries the workspace schema
 * version and is parsed with the same schema a cold load uses, so a stale or
 * hand-edited one reads as absent and the client falls back to a full
 * catch-up. Beyond that the protocol already guards the join: a `commands`
 * frame whose `fromVersion` does not meet the client's version is refused and
 * re-pulled, an ack whose arithmetic does not add up forces a resync, and a
 * version ahead of the server's head is answered with a snapshot. A resumed
 * prefix that is wrong in any way those can see is discarded rather than
 * merged.
 *
 * ## Writes are debounced, and one is forced
 *
 * The prefix moves on every ack, and writing a whole workspace that often would
 * cost more than the replay it saves. Writes are therefore debounced, with a
 * flush when the page is hidden — the same shape `persistence.ts` uses, and for
 * the same reason: the last edit before a tab is closed is the one most worth
 * keeping.
 */

import type { WorkspaceState } from '@beerwright/commands';
import { WORKSPACE_SCHEMA_VERSION, workspaceStateSchema } from '@beerwright/commands';
import type { KeyValueStore } from '@/store/idb';
import { openKeyValueStore } from '@/store/idb';
import { confirmedKeyFor, DATABASE_NAME, STORE_NAME } from '@/store/persistence';

/** How long a moved prefix waits before it is written. */
export const CONFIRMED_DEBOUNCE_MS = 2000;

/** What is written. `version` is the log position `state` is the result of. */
interface ConfirmedRecord {
  readonly schemaVersion: number;
  readonly version: number;
  readonly state: WorkspaceState;
}

/** The prefix as it was left, or `null` for "start from nothing". */
export interface ConfirmedPrefix {
  readonly version: number;
  readonly state: WorkspaceState;
}

export class ConfirmedStore {
  readonly #key: string;
  readonly #store: KeyValueStore;
  readonly #debounceMs: number;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #pending: ConfirmedPrefix | null = null;
  #inFlight: Promise<void> = Promise.resolve();

  constructor(
    owner: string,
    store: KeyValueStore = openKeyValueStore(DATABASE_NAME, STORE_NAME),
    debounceMs: number = CONFIRMED_DEBOUNCE_MS,
  ) {
    this.#key = confirmedKeyFor(owner);
    this.#store = store;
    this.#debounceMs = debounceMs;
  }

  /**
   * Read the prefix, or `null` when there is none worth having.
   *
   * Every failure reads as `null` — a missing record, an unreadable one, a
   * schema that has moved, or a state that no longer validates. The cost of
   * being wrong here is a full catch-up, which is exactly what the client did
   * before this existed; the cost of trusting a bad record is a workspace
   * built on a base the server never had.
   */
  async load(): Promise<ConfirmedPrefix | null> {
    try {
      const record = await this.#store.get<ConfirmedRecord>(this.#key);
      if (record === undefined) return null;
      if (record.schemaVersion !== WORKSPACE_SCHEMA_VERSION) return null;
      if (!Number.isInteger(record.version) || record.version < 0) return null;
      const parsed = workspaceStateSchema.safeParse(record.state);
      if (!parsed.success) return null;
      return { version: record.version, state: parsed.data };
    } catch {
      return null;
    }
  }

  /** Note a moved prefix; schedules a write. */
  save(prefix: ConfirmedPrefix): void {
    this.#pending = prefix;
    this.#clearTimer();
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#writePending();
    }, this.#debounceMs);
  }

  /** Write immediately, cancelling any pending debounce. */
  async flush(): Promise<void> {
    this.#clearTimer();
    this.#writePending();
    await this.#inFlight;
  }

  /** Forget the prefix — the next boot starts from nothing. */
  async clear(): Promise<void> {
    this.#clearTimer();
    this.#pending = null;
    try {
      await this.#store.delete(this.#key);
    } catch {
      // A prefix that cannot be deleted is still validated on the way back in.
    }
  }

  /** Drop any scheduled write. The record already on disk is left alone. */
  dispose(): void {
    this.#clearTimer();
    this.#pending = null;
  }

  #writePending(): void {
    const next = this.#pending;
    this.#pending = null;
    if (next === null) return;
    const record: ConfirmedRecord = {
      schemaVersion: WORKSPACE_SCHEMA_VERSION,
      version: next.version,
      state: next.state,
    };
    this.#inFlight = this.#store.set(this.#key, record).catch(() => undefined);
  }

  #clearTimer(): void {
    if (this.#timer !== null) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }
}
